import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Shield, Search, Plus, RefreshCw, X, CheckCircle, AlertTriangle } from 'lucide-react';
import api from '../../api';
import { toast } from 'sonner';

const COVIDScreeningPage = () => {
  const [screenings, setScreenings] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    patientId: '', patientName: '', screeningDate: new Date().toISOString().split('T')[0],
    temperature: '', symptoms: [], exposureRisk: 'None', vaccinated: false,
    testResult: '', notes: '', status: 'Completed'
  });

  const symptomsList = ['Fever', 'Cough', 'Shortness of Breath', 'Fatigue', 'Body Aches', 'Headache', 'Loss of Taste/Smell', 'Sore Throat', 'Congestion', 'Nausea'];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [docsRes, patientsRes] = await Promise.all([
        api.get('/visit-documents/type/COVID'),
        api.get('/patients')
      ]);
      if (docsRes.data.success) setScreenings(docsRes.data.documents || []);
      if (patientsRes.data.success) setPatients(patientsRes.data.patients || []);
    } catch (error) {
      toast.error('Failed to load screenings');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.patientName) { toast.error('Please select a patient'); return; }
    try {
      const res = await api.post('/visit-documents', {
        ...formData,
        discipline: 'COVID',
        documentType: 'COVID-19 Screening',
        visitDate: formData.screeningDate
      });
      if (res.data.success) {
        toast.success('COVID screening recorded');
        setScreenings(prev => [...prev, res.data.document]);
        setShowModal(false);
        setFormData({
          patientId: '', patientName: '', screeningDate: new Date().toISOString().split('T')[0],
          temperature: '', symptoms: [], exposureRisk: 'None', vaccinated: false,
          testResult: '', notes: '', status: 'Completed'
        });
      }
    } catch (error) {
      toast.error('Failed to record screening');
    }
  };

  const toggleSymptom = (symptom) => {
    setFormData(prev => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom) 
        ? prev.symptoms.filter(s => s !== symptom)
        : [...prev.symptoms, symptom]
    }));
  };

  const filteredScreenings = screenings.filter(s =>
    s.patientName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: screenings.length,
    clear: screenings.filter(s => !s.symptoms?.length && s.testResult !== 'Positive').length,
    symptomatic: screenings.filter(s => s.symptoms?.length > 0).length,
    positive: screenings.filter(s => s.testResult === 'Positive').length
  };

  return (
    <DashboardLayout>
      <div className="p-6" data-testid="covid-screening-page">
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Shield className="w-6 h-6 text-blue-600" />
                COVID-19 Screening
              </h1>
              <p className="text-gray-500 text-sm mt-1">Document patient COVID-19 screenings and test results</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={fetchData} variant="outline"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />New Screening
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-700">{stats.total}</div>
            <div className="text-sm text-blue-600">Total Screenings</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-700">{stats.clear}</div>
            <div className="text-sm text-green-600">Clear</div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-700">{stats.symptomatic}</div>
            <div className="text-sm text-yellow-600">Symptomatic</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-2xl font-bold text-red-700">{stats.positive}</div>
            <div className="text-sm text-red-600">Positive Tests</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search screenings..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : filteredScreenings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No COVID screenings found. Click "New Screening" to add one.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Temperature</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Symptoms</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Test Result</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredScreenings.map((s) => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 font-medium">{s.patientName}</td>
                    <td className="px-6 py-4">{s.visitDate || s.screeningDate}</td>
                    <td className="px-6 py-4">{s.temperature ? `${s.temperature}°F` : '-'}</td>
                    <td className="px-6 py-4">
                      {s.symptoms?.length > 0 ? (
                        <span className="flex items-center gap-1 text-yellow-600">
                          <AlertTriangle className="w-4 h-4" />{s.symptoms.length} symptom(s)
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-green-600">
                          <CheckCircle className="w-4 h-4" />None
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        s.testResult === 'Positive' ? 'bg-red-100 text-red-800' :
                        s.testResult === 'Negative' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {s.testResult || 'Not Tested'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">{s.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 m-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">COVID-19 Screening</h2>
                <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4 max-h-[60vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium mb-1">Patient *</label>
                  <select value={formData.patientId} onChange={(e) => {
                    const p = patients.find(p => p.id === e.target.value);
                    setFormData(prev => ({ ...prev, patientId: e.target.value, patientName: p ? `${p.firstName} ${p.lastName}` : '' }));
                  }} className="w-full border rounded-md px-3 py-2">
                    <option value="">Select Patient</option>
                    {patients.map(p => <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Screening Date</label>
                    <Input type="date" value={formData.screeningDate} onChange={(e) => setFormData(prev => ({ ...prev, screeningDate: e.target.value }))} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Temperature (°F)</label>
                    <Input type="number" step="0.1" value={formData.temperature} onChange={(e) => setFormData(prev => ({ ...prev, temperature: e.target.value }))} placeholder="98.6" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Symptoms (check all that apply)</label>
                  <div className="grid grid-cols-2 gap-2">
                    {symptomsList.map(symptom => (
                      <label key={symptom} className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={formData.symptoms.includes(symptom)} onChange={() => toggleSymptom(symptom)} />
                        {symptom}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Exposure Risk</label>
                    <select value={formData.exposureRisk} onChange={(e) => setFormData(prev => ({ ...prev, exposureRisk: e.target.value }))} className="w-full border rounded-md px-3 py-2">
                      <option>None</option>
                      <option>Low</option>
                      <option>Moderate</option>
                      <option>High</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Test Result</label>
                    <select value={formData.testResult} onChange={(e) => setFormData(prev => ({ ...prev, testResult: e.target.value }))} className="w-full border rounded-md px-3 py-2">
                      <option value="">Not Tested</option>
                      <option>Negative</option>
                      <option>Positive</option>
                      <option>Pending</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={formData.vaccinated} onChange={(e) => setFormData(prev => ({ ...prev, vaccinated: e.target.checked }))} />
                  <label className="text-sm">Patient is vaccinated</label>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Notes</label>
                  <textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} className="w-full border rounded-md px-3 py-2" rows={2} />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSubmit}>Save Screening</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default COVIDScreeningPage;
